// ===========================================
// Reports Overview Page
// ===========================================
// Dashboard showing key insights from all report types
// Provides quick access to individual reports
// Phase 2 Priority 12: Reporting & Analytics

import React from 'react';
import { Email as EmailIcon, History as HistoryIcon } from '@mui/icons-material';
import { useEmailLogReport, useRetroEditsReport } from '../../../hooks/useReports';
import { useCurrentTerm } from '../../../hooks/useSchoolWeek';
import { defaultEmailLogFilters } from './EmailLogReportPage';
import { defaultRetroEditsFilters } from './RetroEditsReportPage';
import { Box, Grid, Card, CardContent, Typography, Button, Skeleton } from '@mui/material';
import {
  Assessment,
  AttachMoney,
  Receipt,
  School,
  Person,
  CalendarMonth,
  TrendingUp,
  Work,
  ArrowForward,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../../components/common/PageHeader';
import { formatCurrencyFromCents } from '../../../utils/currency';
import { useReportsSettings } from '../../../hooks/useAdminSettings';
import { usePermissions } from '../../../hooks/usePermissions';
import { REPORT_KEYS, type ReportKey } from '../../../constants/reportKeys.constants';
import {
  useAttendanceOverview,
  useRevenueOverview,
  useOutstandingInvoicesOverview,
  useStudentProgressOverview,
  useTeacherPerformanceOverview,
  useHybridBookingOverview,
  useEnrollmentTrends,
  useOperationsOverview,
} from '../../../hooks/useReports';

interface ReportCardProps {
  title: string;
  icon: React.ReactNode;
  highlights: Array<{ label: string; value: string | number; color?: string }>;
  route: string;
  isLoading?: boolean;
}

// A report whose page no longer lives under /reports (resolved relative to the Reports page).
const CARD_PATH_OVERRIDES: Record<string, string> = { 'email-log': '../email-log' };

const ReportCard: React.FC<ReportCardProps> = ({ title, icon, highlights, route, isLoading }) => {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            {icon}
            <Typography variant="h6" sx={{ ml: 1 }}>
              <Skeleton width={150} />
            </Typography>
          </Box>
          <Skeleton variant="rectangular" height={100} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4,
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box sx={{ color: 'primary.main' }}>{icon}</Box>
          <Typography variant="h6" sx={{ ml: 1 }}>
            {title}
          </Typography>
        </Box>

        <Box sx={{ flexGrow: 1, mb: 2 }}>
          {highlights.map((highlight, index) => (
            <Box key={index} sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                {highlight.label}
              </Typography>
              <Typography
                variant="h6"
                sx={{ color: highlight.color || 'text.primary', fontWeight: 600 }}
              >
                {highlight.value}
              </Typography>
            </Box>
          ))}
        </Box>

        <Button
          variant="outlined"
          endIcon={<ArrowForward />}
          onClick={() => navigate(CARD_PATH_OVERRIDES[route] ?? route)}
          fullWidth
          sx={{ mt: 'auto' }}
        >
          View Full Report
        </Button>
      </CardContent>
    </Card>
  );
};

const ReportsOverviewPage: React.FC = () => {
  // The page needs its own navigate: the one inside ReportCard belongs to that
  // child component and is not in scope here.
  const navigate = useNavigate();

  // Which cards the admin has chosen to show (Report settings → Overview).
  //
  // ⚠️ Every card hook below is gated on this, so hiding a card SKIPS ITS
  // FETCH rather than merely hiding rendered markup — before this, all ten
  // queries fired unconditionally and hiding a card saved nothing.
  //
  // ⚠️ `settingsSettled` is what keeps the three states apart. A disabled
  // React Query v5 query reports `isLoading: false, isPending: true`, so
  // gating a card's skeleton on `isLoading` alone would render a ZERO-STATE
  // while the settings are still in flight. And an empty `visibleCards` is a
  // real saved choice ("hide everything"), not an absent value — so it must
  // never fall back to showing all nine.
  const { data: reportSettings, isPending: settingsPending, isError: settingsError } =
    useReportsSettings();
  // On a settings failure, fall back to showing every card: a broken settings
  // read must not make the Overview look empty.
  const settingsSettled = !settingsPending;
  const visibleCards = React.useMemo<string[] | null>(() => {
    if (settingsError) return [...REPORT_KEYS];
    if (!reportSettings) return null;
    return reportSettings.visibleCards;
  }, [reportSettings, settingsError]);

  // The Retro Edits card is the owner's whatever the school-wide selection says (roles slice 2,
  // V25): for anyone else it is neither shown nor queried (the server would 403 it).
  const { isOwner, can } = usePermissions();
  // The Email Log lives under Communications now: its endpoint needs communications_emails.read,
  // so the card is neither shown nor queried for a reports-only user (it would 403).
  const canReadEmailLog = can('communications_emails', 'read');
  const shows = React.useCallback(
    (key: ReportKey) =>
      visibleCards !== null &&
      visibleCards.includes(key) &&
      (key !== 'retro-edits' || isOwner) &&
      (key !== 'email-log' || canReadEmailLog),
    [visibleCards, isOwner, canReadEmailLog]
  );

  const { data: attendanceData, isLoading: attendanceLoading } = useAttendanceOverview(
    shows('attendance')
  );
  // The last 30 days, one row, no table — computed per mount, not at module load.
  const [emailLogOverviewFilters] = React.useState(() => ({ ...defaultEmailLogFilters(), page: 1, pageSize: 1 }));
  // pageSize 1: the card reads `summary` only, which the backend computes over
  // the whole PERIOD regardless of paging, so there is no reason to fetch rows.
  const [retroEditsOverviewFilters] = React.useState(() => ({ ...defaultRetroEditsFilters(), page: 1, pageSize: 1 }));
  const { data: retroEditsData, isLoading: retroEditsLoading } = useRetroEditsReport(
    retroEditsOverviewFilters,
    shows('retro-edits')
  );
  const { data: emailLogData, isLoading: emailLogLoading } = useEmailLogReport(
    emailLogOverviewFilters,
    shows('email-log')
  );
  // ⚠️ Revenue and outstanding invoices stay ENABLED regardless of their card,
  // because the quick-stats banner reads them and the banner is always shown
  // (deliberately not configurable). Gating these on card visibility would
  // blank two banner tiles the admin never chose to hide.
  const { data: revenueData, isLoading: revenueLoading } = useRevenueOverview();
  const { data: invoicesData, isLoading: invoicesLoading } = useOutstandingInvoicesOverview();
  const { data: studentProgressData, isLoading: studentProgressLoading } =
    useStudentProgressOverview(shows('student-progress'));
  const { data: teacherPerformanceData, isLoading: teacherPerformanceLoading } =
    useTeacherPerformanceOverview(shows('teacher-performance'));
  const { term: currentTerm, isLoading: currentTermLoading } = useCurrentTerm();
  const { data: hybridBookingData, isLoading: hybridBookingLoading } = useHybridBookingOverview(
    currentTerm?.id,
    shows('hybrid-booking')
  );
  // ⚠️ The quick-stats banner reads enrolment and revenue too, and the banner is
  // ALWAYS shown (it is deliberately not configurable). So these two stay
  // enabled regardless of their card's visibility, or the banner would blank.
  const { data: enrollmentData, isLoading: enrollmentLoading } = useEnrollmentTrends(12);
  const { data: operationsData, isLoading: operationsLoading } = useOperationsOverview(
    shows('operations')
  );

  return (
    <Box>
      <PageHeader
        title="Reports Overview"
        subtitle="Key insights and metrics across all reporting areas"
        icon={<Assessment />}
      />

      {/* Quick Stats Banner */}
      <Card sx={{ mb: 3, bgcolor: 'primary.main', color: 'white' }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Overall Attendance Rate
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {attendanceLoading ? (
                  <Skeleton sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} width={80} />
                ) : (
                  `${attendanceData?.overallAttendanceRate?.toFixed(1) || 0}%`
                )}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Total Revenue (MTD)
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {revenueLoading ? (
                  <Skeleton sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} width={100} />
                ) : (
                  formatCurrencyFromCents(revenueData?.totalRevenue ?? 0)
                )}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Active Students
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {enrollmentLoading ? (
                  <Skeleton sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} width={60} />
                ) : (
                  // Distinct students, NOT enrolment rows: this tile used to
                  // render totalActiveEnrollments, so a child in two classes
                  // was counted twice under a label promising students.
                  enrollmentData?.summary?.totalActiveStudents ?? 0
                )}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Outstanding Invoices
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {invoicesLoading ? (
                  <Skeleton sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} width={100} />
                ) : (
                  formatCurrencyFromCents(invoicesData?.totalOutstanding ?? 0)
                )}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Report Cards Grid.
          ⚠️ Three states, kept apart deliberately. While the settings query is
          in flight we render skeletons — NOT an empty grid, which would flash a
          false "you have hidden everything". Once settled, an empty selection is
          a real saved choice and gets its own message. */}
      {!settingsSettled ? (
        <Grid container spacing={3}>
          {[0, 1, 2].map((i) => (
            <Grid item xs={12} md={6} lg={4} key={i}>
              <Skeleton variant="rectangular" height={260} />
            </Grid>
          ))}
        </Grid>
      ) : visibleCards !== null && REPORT_KEYS.every((key) => !shows(key)) ? (
        // Counted through `shows`, not the stored list: a non-owner whose only selected card is
        // Retro Edits sees none, and gets this message rather than a blank page (step 9 /qa I6).
        <Card variant="outlined">
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" gutterBottom>
              No report cards are shown
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Every card is hidden. Choose which reports appear here in Report settings — all
              reports remain available from the Reports menu.
            </Typography>
            <Button variant="outlined" onClick={() => navigate('settings')}>
              Open Report settings
            </Button>
          </CardContent>
        </Card>
      ) : (
      <Grid container spacing={3} data-testid="report-cards">
        {/* Attendance Report */}
        {shows('attendance') && (
        <Grid item xs={12} md={6} lg={4}>
          <ReportCard
            title="Attendance"
            icon={<Assessment fontSize="large" />}
            highlights={[
              {
                label: 'Attendance Rate',
                value: `${attendanceData?.overallAttendanceRate?.toFixed(1) || 0}%`,
                color:
                  (attendanceData?.overallAttendanceRate || 0) >= 85
                    ? 'success.main'
                    : (attendanceData?.overallAttendanceRate || 0) >= 70
                    ? 'warning.main'
                    : 'error.main',
              },
              {
                label: 'Total Lessons',
                value: attendanceData?.totalLessons || 0,
              },
              {
                label: 'Absences',
                value: attendanceData?.totalAbsences || 0,
              },
            ]}
            route="attendance"
            isLoading={attendanceLoading}
          />
        </Grid>

        )}

        {/* Revenue Report */}
        {shows('revenue') && (
        <Grid item xs={12} md={6} lg={4}>
          <ReportCard
            title="Revenue"
            icon={<AttachMoney fontSize="large" />}
            highlights={[
              {
                label: 'Total Revenue',
                value: formatCurrencyFromCents(revenueData?.totalRevenue ?? 0),
                color: 'success.main',
              },
              {
                label: 'MRR (Monthly Recurring)',
                value: formatCurrencyFromCents(revenueData?.mrr ?? 0),
              },
              {
                // Average INVOICE value, not per-student revenue: ARPS is a
                // separate endpoint that requires a termId, which this
                // school-wide overview has no context for.
                label: 'Avg Invoice Value',
                value: formatCurrencyFromCents(revenueData?.averageInvoiceValue ?? 0),
              },
            ]}
            route="revenue"
            isLoading={revenueLoading}
          />
        </Grid>

        )}

        {/* Outstanding Invoices */}
        {shows('outstanding-invoices') && (
        <Grid item xs={12} md={6} lg={4}>
          <ReportCard
            title="Outstanding Invoices"
            icon={<Receipt fontSize="large" />}
            highlights={[
              {
                label: 'Total Outstanding',
                value: formatCurrencyFromCents(invoicesData?.totalOutstanding ?? 0),
                color: 'error.main',
              },
              {
                label: 'Invoice Count',
                value: invoicesData?.invoiceCount || 0,
              },
              {
                label: '90+ Days Overdue',
                value: formatCurrencyFromCents(invoicesData?.over90Days ?? 0),
              },
            ]}
            route="outstanding-invoices"
            isLoading={invoicesLoading}
          />
        </Grid>

        )}

        {/* Student Progress */}
        {shows('student-progress') && (
        <Grid item xs={12} md={6} lg={4}>
          <ReportCard
            title="Student Progress"
            icon={<School fontSize="large" />}
            highlights={[
              {
                label: 'Average Progress',
                value: `${studentProgressData?.averageProgress?.toFixed(1) || 0}%`,
                color: 'primary.main',
              },
              {
                label: 'Pieces Completed',
                value: studentProgressData?.piecesCompleted || 0,
              },
              {
                label: 'Active Students',
                value: studentProgressData?.activeStudents || 0,
              },
            ]}
            route="student-progress"
            isLoading={studentProgressLoading}
          />
        </Grid>

        )}

        {/* Teacher Performance */}
        {shows('teacher-performance') && (
        <Grid item xs={12} md={6} lg={4}>
          <ReportCard
            title="Teacher Performance"
            icon={<Person fontSize="large" />}
            highlights={[
              {
                // Same number as the dashboard's "Teacher Tasks · Outstanding" widget.
                label: 'Outstanding Tasks',
                value: teacherPerformanceData?.outstandingTasks ?? 0,
                color: (teacherPerformanceData?.outstandingTasks ?? 0) > 0 ? 'error.main' : 'success.main',
              },
              {
                label: 'Avg Attendance Rate',
                value: `${teacherPerformanceData?.avgAttendanceRate?.toFixed(1) || 0}%`,
                color: 'primary.main',
              },
              {
                label: 'Total Classes',
                value: teacherPerformanceData?.totalClasses || 0,
              },
            ]}
            route="teacher-performance"
            isLoading={teacherPerformanceLoading}
          />
        </Grid>

        )}

        {/* Hybrid Booking Analytics */}
        {shows('hybrid-booking') && (
        <Grid item xs={12} md={6} lg={4}>
          <ReportCard
            title="Hybrid Booking Analytics"
            icon={<CalendarMonth fontSize="large" />}
            highlights={[
              {
                label: currentTerm ? `Completion Rate · ${currentTerm.name}` : 'Completion Rate',
                value: `${hybridBookingData?.completionRate?.toFixed(1) || 0}%`,
                color: 'secondary.main',
              },
              {
                label: 'Total Bookings',
                value: hybridBookingData?.totalBookings || 0,
              },
              {
                label: 'Avg Booking Time',
                value: `${hybridBookingData?.avgBookingTime || 0}h`,
              },
            ]}
            route="hybrid-booking"
            isLoading={currentTermLoading || (!!currentTerm && hybridBookingLoading)}
          />
        </Grid>

        )}

        {/* Enrollment Analytics */}
        {shows('enrollment') && (
        <Grid item xs={12} md={6} lg={4}>
          <ReportCard
            title="Enrolment Analytics"
            icon={<TrendingUp fontSize="large" />}
            highlights={[
              {
                label: 'Active Students',
                value: enrollmentData?.summary?.totalActiveStudents ?? 0,
                color: 'success.main',
              },
              {
                // The enrolment count kept beside the headcount, honestly
                // labelled — the two differ by students taking more than one class.
                label: 'Active Enrolments',
                value: enrollmentData?.summary?.totalActiveEnrollments ?? 0,
              },
              {
                label: 'New This Month',
                value: enrollmentData?.summary?.newEnrollmentsThisMonth || 0,
              },
              {
                label: 'Retention Rate',
                value: `${enrollmentData?.summary?.retentionRate?.toFixed(1) || 0}%`,
              },
            ]}
            route="enrollment"
            isLoading={enrollmentLoading}
          />
        </Grid>

        )}

        {/* Operational Efficiency */}
        {shows('operations') && (
        <Grid item xs={12} md={6} lg={4}>
          <ReportCard
            title="Operational Efficiency"
            icon={<Work fontSize="large" />}
            highlights={[
              {
                label: 'Room Utilization',
                value: `${operationsData?.avgRoomUtilization?.toFixed(1) || 0}%`,
                color: 'primary.main',
              },
              {
                label: 'Meet & Greet Conversion',
                value: `${operationsData?.meetGreetConversion?.toFixed(1) || 0}%`,
              },
              {
                label: 'Extra Lesson Requests',
                value: operationsData?.extraLessonRequests || 0,
              },
            ]}
            route="operations"
            isLoading={operationsLoading}
          />
        </Grid>

        )}

        {/* Email Log (2026-09-09) */}
        {shows('email-log') && (
        <Grid item xs={12} md={6} lg={4}>
          <ReportCard
            title="Email Log"
            icon={<EmailIcon fontSize="large" />}
            highlights={[
              {
                label: 'Emails (30 days)',
                value: emailLogData?.summary.total ?? 0,
              },
              {
                label: 'Not sent',
                value: emailLogData?.summary.notSent ?? 0,
                color: (emailLogData?.summary.notSent ?? 0) > 0 ? 'error.main' : 'success.main',
              },
              {
                label: 'Delivered',
                value: `${emailLogData?.summary.deliveryRate?.toFixed(1) ?? 0}%`,
              },
            ]}
            route="email-log"
            isLoading={emailLogLoading}
          />
        </Grid>
        )}

        {/* Retro Edits (2026-09-18) — the supervision card.
            ⚠️ This block is the EIGHTH registration site and the only one that
            fails SILENTLY: the page hand-writes one block per report, so
            omitting it renders no card while the parity test, the settings
            page and every other check stay green. */}
        {shows('retro-edits') && (
        <Grid item xs={12} md={6} lg={4}>
          <ReportCard
            title="Retro Edits"
            icon={<HistoryIcon fontSize="large" />}
            highlights={[
              {
                label: 'Retroactive (30 days)',
                value: retroEditsData?.summary.retroactiveEdits ?? 0,
              },
              {
                label: 'Deletions',
                value: retroEditsData?.summary.totalDeletions ?? 0,
              },
              {
                // The owner's threshold question: was this recorded near the
                // time? Past a week it plainly was not.
                label: 'Worst lag (days)',
                value: retroEditsData?.summary.worstLagDays ?? 0,
                color: (retroEditsData?.summary.worstLagDays ?? 0) > 7 ? 'error.main' : 'success.main',
              },
            ]}
            route="retro-edits"
            isLoading={retroEditsLoading}
          />
        </Grid>
        )}
      </Grid>
      )}

      {/* Help Text — suppressed while loading and when nothing is shown, so it
          never tells the reader to click cards that are not there. */}
      {settingsSettled && visibleCards !== null && visibleCards.length > 0 && (
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Click on any report card to view detailed analytics and export data
            {visibleCards.length < REPORT_KEYS.length && ' · Some cards are hidden (Report settings)'}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ReportsOverviewPage;
